import React, { useState, useRef, useEffect } from "react";
import { MousePointerClick, ChevronDown } from "lucide-react";
import Icon from "../../../../shared/components/Icon";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import EventPropertyFilterModule from "../../../conversion-events/components/EventPropertyFilterModule";
import { EventPropertyFilterChip } from "../../../conversion-events/components/EventsFilterModule";
import TransactionPropertyFilterModule from "../../../conversion-events/components/TransactionPropertyFilterModule";
import TransactionPropertyFilterChip from "../../../conversion-events/components/TransactionPropertyFilterChip";
import { formatNumber } from "../../../../shared/utils/numberFormatter";
import EventsSelectorMenu from "./EventsSelectorMenu";

/**
 * MetricBlock
 * Represents a single metric (A, B, C...) that can contain multiple events.
 * All events are aggregated together.
 */
export default function MetricBlock({
  metric,
  metricIndex,
  availableEvents = [],
  teamId,
  onUpdate,
  onRemove,
  onAddEvent,
  onRemoveEvent,
  onUpdateEventFilters,
}) {
  const [eventMenuOpen, setEventMenuOpen] = useState(false);
  const [aggregationOpen, setAggregationOpen] = useState(false);
  const [isPropertyFilterOpen, setIsPropertyFilterOpen] = useState(false);
  const [isTransactionFilterOpen, setIsTransactionFilterOpen] = useState(false);
  const [selectedEventForFilter, setSelectedEventForFilter] = useState(null);
  const [selectedEventIndex, setSelectedEventIndex] = useState(null);
  const [editingPropertyFilter, setEditingPropertyFilter] = useState(null);
  const [isEditingMetricName, setIsEditingMetricName] = useState(false);
  const [editingMetricNameValue, setEditingMetricNameValue] = useState("");
  const metricNameInputRef = useRef(null);

  const addEventButtonRef = useRef(null);
  const eventMenuRef = useRef(null);
  const aggregationMenuRef = useRef(null);
  const filterButtonRefs = useRef({});
  const propertyFilterButtonRefs = useRef({});

  // Transaction-only metrics: Total Transactions, Unique Users, Total Value. Else: Total Events, Unique Users, Total Sessions.
  const isTransactionOnly =
    metric.events &&
    Array.isArray(metric.events) &&
    metric.events.length > 0 &&
    metric.events.every((e) => e.eventType === "transaction" || e.source === "transaction");
  const aggregations = isTransactionOnly
    ? [
        { id: "count", label: "Total Transactions" },
        { id: "uniques", label: "Unique Users" },
        { id: "value", label: "Total Value" },
      ]
    : [
        { id: "count", label: "Total Events" },
        { id: "uniques", label: "Unique Users" },
        { id: "sessions", label: "Total Sessions" },
      ];

  // Close popovers on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (eventMenuRef.current && !eventMenuRef.current.contains(e.target) && !addEventButtonRef.current?.contains(e.target)) {
        setEventMenuOpen(false);
      }
      if (aggregationMenuRef.current && !aggregationMenuRef.current.contains(e.target)) {
        setAggregationOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const metricId = String.fromCharCode(65 + metricIndex); // A, B, C...

  const getEventIcon = (eventName) => {
    const lower = (eventName || "").toLowerCase();
    if (lower.includes("click")) return MousePointerClick;
    return MousePointerClick;
  };

  const handleAddEventClick = () => setEventMenuOpen((o) => !o);

  // When metric becomes transaction-only and current aggregation is sessions, switch to count (Total Transactions)
  useEffect(() => {
    if (isTransactionOnly && metric.aggregation === "sessions") {
      onUpdate(metricIndex, { ...metric, aggregation: "count" });
    }
  }, [isTransactionOnly, metric.aggregation]); // eslint-disable-line react-hooks/exhaustive-deps -- only normalize when aggregation is invalid for transaction

  const handleSelectEvent = (eventName, source = 'event') => {
    onAddEvent(metricIndex, eventName, source);
    setEventMenuOpen(false);
  };

  const handleAggregationChange = (aggId) => {
    onUpdate(metricIndex, { ...metric, aggregation: aggId });
    setAggregationOpen(false);
  };

  const handleFilterClick = (eventDef, eventIndex) => {
    const source = eventDef.source || 'event';
    setSelectedEventForFilter({
      id: `${eventDef.name}\0${source}`,
      eventName: eventDef.name,
      eventType: source === 'transaction' ? 'transaction' : 'custom',
      source,
    });
    setSelectedEventIndex(eventIndex);
    if (source === 'transaction') {
      setIsTransactionFilterOpen(true);
    } else {
      setIsPropertyFilterOpen(true);
    }
  };

  const handleFiltersChange = (filter) => {
    if (selectedEventIndex !== null && selectedEventForFilter) {
      // Get current filters for this event
      const currentEvent = metric.events[selectedEventIndex];
      const currentFilters = currentEvent.filters || [];
      
      // Convert filter from EventPropertyFilterModule format to insights format
      const insightsFilter = {
        field: filter.property || filter.field,
        property: filter.property || filter.field, // queryCompiler accepts both
        operator: filter.operator,
        value: filter.value,
        value2: filter.value2,
        propertyType: filter.propertyType,
        propertySource: filter.propertySource,
        propertyLabel: filter.propertyLabel || filter.property || filter.field,
        category: 'event_properties',
        eventName: selectedEventForFilter.eventName,
        listMode: filter.listMode,
        listItemType: filter.listItemType,
        sampleValues: filter.sampleValues || [],
        id: filter.id || `filter-${Date.now()}-${Math.random()}`,
        connector: currentFilters.length === 0 ? 'WHERE' : 'AND' // First filter is WHERE, others are AND
      };
      
      // If editing existing filter
      if (editingPropertyFilter) {
        const updatedFilters = currentFilters.map(f => 
          f.id === editingPropertyFilter.id ? insightsFilter : f
        );
        onUpdateEventFilters(metricIndex, selectedEventIndex, updatedFilters);
        setEditingPropertyFilter(null);
      } else {
        // Add new filter
        onUpdateEventFilters(metricIndex, selectedEventIndex, [...currentFilters, insightsFilter]);
      }
      
      setIsPropertyFilterOpen(false);
      setSelectedEventForFilter(null);
      setSelectedEventIndex(null);
    }
  };

  const handleFirstTimeChange = (enabled, eventName, eventId) => {
    if (selectedEventIndex !== null && selectedEventForFilter) {
      const currentEvent = metric.events[selectedEventIndex];
      const currentFilters = currentEvent.filters || [];
      
      if (enabled) {
        // Remove any existing first_time filter first
        const withoutFirstTime = currentFilters.filter(f => f.category !== 'first_time');
        // Add the first time filter
        const firstTimeFilter = {
          id: 'first-time-filter',
          field: 'firstTime',
          property: 'firstTime',
          operator: 'equals', // Required for query compiler, but firstTime is handled specially
          value: true,
          category: 'first_time',
          eventName: selectedEventForFilter.eventName,
          connector: withoutFirstTime.length === 0 ? 'WHERE' : 'AND'
        };
        onUpdateEventFilters(metricIndex, selectedEventIndex, [...withoutFirstTime, firstTimeFilter]);
      } else {
        // Remove the first time filter
        const withoutFirstTime = currentFilters.filter(f => f.category !== 'first_time');
        onUpdateEventFilters(metricIndex, selectedEventIndex, withoutFirstTime);
      }
      
      setIsPropertyFilterOpen(false);
      setSelectedEventForFilter(null);
      setSelectedEventIndex(null);
    }
  };

  const handleTransactionFiltersChange = (filter) => {
    if (selectedEventIndex === null || !selectedEventForFilter) return;
    const currentEvent = metric.events[selectedEventIndex];
    const currentFilters = currentEvent.filters || [];
    const insightsFilter = {
      ...filter,
      field: filter.property || filter.field,
      property: filter.property || filter.field,
      eventName: selectedEventForFilter.eventName,
      connector: editingPropertyFilter ? (filter.connector || (currentFilters.length === 0 ? 'WHERE' : 'AND')) : (currentFilters.length === 0 ? 'WHERE' : 'AND'),
      category: 'transaction_properties',
    };
    let updatedFilters;
    if (editingPropertyFilter) {
      updatedFilters = currentFilters.map(f => (f.id === editingPropertyFilter.id ? insightsFilter : f));
      setEditingPropertyFilter(null);
    } else {
      updatedFilters = [...currentFilters, insightsFilter];
    }
    onUpdateEventFilters(metricIndex, selectedEventIndex, updatedFilters);
    setIsTransactionFilterOpen(false);
    setSelectedEventForFilter(null);
    setSelectedEventIndex(null);
  };

  const handleTransactionFirstTimeChange = (enabled) => {
    if (selectedEventIndex !== null && selectedEventForFilter) {
      const currentEvent = metric.events[selectedEventIndex];
      const currentFilters = currentEvent.filters || [];
      if (enabled) {
        const withoutFirstTime = currentFilters.filter(f => f.category !== 'first_time');
        const firstTimeFilter = {
          id: 'first-time-filter',
          field: 'firstTime',
          property: 'firstTime',
          operator: 'equals',
          value: true,
          category: 'first_time',
          eventName: selectedEventForFilter.eventName,
          connector: withoutFirstTime.length === 0 ? 'WHERE' : 'AND',
        };
        onUpdateEventFilters(metricIndex, selectedEventIndex, [...withoutFirstTime, firstTimeFilter]);
      } else {
        const withoutFirstTime = currentFilters.filter(f => f.category !== 'first_time');
        onUpdateEventFilters(metricIndex, selectedEventIndex, withoutFirstTime);
      }
      setIsTransactionFilterOpen(false);
      setSelectedEventForFilter(null);
      setSelectedEventIndex(null);
    }
  };

  const metricLabel = (() => {
    const aggLabel =
      aggregations.find((a) => a.id === metric.aggregation)?.label || "Total Events";
    const events = metric.events || [];
    if (events.length === 0) return `${aggLabel} of ...`;
    if (events.length === 1) return `${aggLabel} of ${events[0].name}`;
    return `${aggLabel} of ${events[0].name} or ${events.length - 1} more`;
  })();

  const displayLabel = (metric.metricName && String(metric.metricName).trim()) ? String(metric.metricName).trim() : metricLabel;

  const startEditingName = () => {
    setEditingMetricNameValue(displayLabel);
    setIsEditingMetricName(true);
    setTimeout(() => metricNameInputRef.current?.focus(), 0);
  };

  const commitMetricName = () => {
    const value = (editingMetricNameValue || "").trim();
    onUpdate(metricIndex, { ...metric, metricName: value || undefined });
    setIsEditingMetricName(false);
    setEditingMetricNameValue("");
  };

  useEffect(() => {
    if (!isEditingMetricName) return;
    const ref = metricNameInputRef.current;
    if (!ref) return;
    const onBlur = () => {
      const raw = ref.value ?? "";
      const value = (typeof raw === "string" ? raw : "").trim();
      onUpdate(metricIndex, { ...metric, metricName: value || undefined });
      setIsEditingMetricName(false);
      setEditingMetricNameValue("");
    };
    ref.addEventListener("blur", onBlur);
    return () => ref.removeEventListener("blur", onBlur);
  }, [isEditingMetricName, metricIndex, metric, onUpdate]);

  return (
    <div className="mb-6 pb-6 border-b" style={{ borderColor: theme.border }}>
      {/* Metric header (A, label, edit, delete) */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-semibold shrink-0" style={{ color: theme.textPrimary }}>
          {metricId}
        </span>
        {isEditingMetricName ? (
          <input
            ref={metricNameInputRef}
            type="text"
            value={editingMetricNameValue}
            onChange={(e) => setEditingMetricNameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitMetricName();
              if (e.key === "Escape") {
                setEditingMetricNameValue(displayLabel);
                setIsEditingMetricName(false);
                metricNameInputRef.current?.blur();
              }
            }}
            className="flex-1 min-w-0 px-2 py-1 text-sm border rounded"
            style={{ color: theme.textPrimary, borderColor: theme.border }}
            placeholder={metricLabel}
          />
        ) : (
          <span className="text-sm flex-1 truncate" style={{ color: theme.textPrimary }}>
            {displayLabel}
          </span>
        )}
        <div className="flex items-center gap-0.5 shrink-0">
          {!isEditingMetricName && (
            <button
              onClick={startEditingName}
              className="p-1 hover:bg-gray-100 rounded"
              title="Rename metric"
            >
              <Icon name="edit_pencil" size={18} color="#6b7280" />
            </button>
          )}
          {onRemove && (
            <button
              onClick={() => onRemove(metricIndex)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Remove metric"
            >
              <Icon name="delete" size={16} color="#4b5563" />
            </button>
          )}
        </div>
      </div>

      {/* Events list */}
      <div className="space-y-2 mt-2 mb-3">
        {(metric.events || []).length === 0 ? (
          <div className="text-xs text-gray-500 italic pl-2">
            No events added. Click &quot;Add Event&quot; below.
          </div>
        ) : (
          (metric.events || []).map((eventDef, eventIndex) => {
            const EventIcon = getEventIcon(eventDef.name);
            const hasFilters = eventDef.filters && eventDef.filters.length > 0;
            const eventFilters = eventDef.filters || [];
            
            return (
              <div key={`${eventDef.name}\0${eventDef.source || 'event'}-${eventIndex}`} className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg flex-1">
                    <EventIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                    <span className="text-sm text-black font-medium truncate">
                      {eventDef.name}
                    </span>
                    {hasFilters && (
                      <span className="text-xs px-1.5 py-0.5 bg-[#4B57AA] text-white rounded">
                        {eventFilters.length}
                      </span>
                    )}
                  </div>
                  <button
                    ref={(el) => {
                      if (el) {
                        filterButtonRefs.current[`${metricIndex}-${eventIndex}`] = el;
                      }
                    }}
                    onClick={() => handleFilterClick(eventDef, eventIndex)}
                    className="p-1.5 hover:bg-gray-100 rounded"
                    title="Filter event"
                    style={{
                      color: hasFilters ? "#4B57AA" : "#6b7280"
                    }}
                  >
                    <Icon name="filter" size={18} color={hasFilters ? "#4B57AA" : "#6b7280"} />
                  </button>
                  <button
                    onClick={() => onRemoveEvent(metricIndex, eventIndex)}
                    className="p-1.5 hover:bg-gray-100 rounded"
                    title="Remove event"
                  >
                    <Icon name="delete" size={16} color="#4b5563" />
                  </button>
                </div>
                
                {/* Display filter chips below each event */}
                {eventFilters.length > 0 && (
                  <div className="ml-6 space-y-2">
                    {eventFilters.map((filter, filterIndex) => {
                      const connector = filter.connector || (filterIndex === 0 ? 'WHERE' : 'AND');
                      
                      // Special handling for first_time filter
                      if (filter.category === 'first_time') {
                        return (
                          <div key={filter.id} className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium text-gray-500 uppercase px-1.5">{connector}</span>
                            <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg border border-gray-200">
                              <span className="text-sm text-gray-900 font-medium">First time only</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedFilters = eventFilters.filter(f => f.id !== filter.id);
                                  onUpdateEventFilters(metricIndex, eventIndex, updatedFilters);
                                }}
                                className="p-1 hover:bg-gray-200 rounded transition-colors text-gray-600 hover:text-gray-900"
                                title="Remove First time only"
                              >
                                <Icon name="delete" size={16} />
                              </button>
                            </div>
                          </div>
                        );
                      }
                      
                      // Create ref object if it doesn't exist
                      if (!propertyFilterButtonRefs.current[filter.id]) {
                        propertyFilterButtonRefs.current[filter.id] = React.createRef();
                      }
                      const isTransactionEvent = eventDef.source === 'transaction';
                      const onRemoveFilter = () => {
                        const updatedFilters = eventFilters.filter(f => f.id !== filter.id);
                        onUpdateEventFilters(metricIndex, eventIndex, updatedFilters);
                      };
                      if (isTransactionEvent) {
                        return (
                          <TransactionPropertyFilterChip
                            key={filter.id}
                            filter={filter}
                            isFirst={filterIndex === 0}
                            onEdit={() => {
                              setEditingPropertyFilter(filter);
                              setSelectedEventForFilter({
                                id: `${eventDef.name}\0transaction`,
                                eventName: eventDef.name,
                                eventType: 'transaction',
                                source: 'transaction',
                              });
                              setSelectedEventIndex(eventIndex);
                              setIsTransactionFilterOpen(true);
                            }}
                            onUpdate={(updates) => {
                              const updatedFilters = eventFilters.map(f =>
                                f.id === filter.id ? { ...f, ...updates } : f
                              );
                              onUpdateEventFilters(metricIndex, eventIndex, updatedFilters);
                            }}
                            onRemove={onRemoveFilter}
                            teamId={teamId}
                          />
                        );
                      }
                      return (
                        <EventPropertyFilterChip
                          key={filter.id}
                          filter={filter}
                          isFirst={filterIndex === 0}
                          filterRef={propertyFilterButtonRefs.current[filter.id]}
                          onEdit={() => {
                            setEditingPropertyFilter(filter);
                            setSelectedEventForFilter({
                              id: eventDef.name,
                              eventName: eventDef.name,
                              eventType: 'custom',
                            });
                            setSelectedEventIndex(eventIndex);
                            setIsPropertyFilterOpen(true);
                          }}
                          onUpdate={(updates) => {
                            const updatedFilters = eventFilters.map(f =>
                              f.id === filter.id ? { ...f, ...updates } : f
                            );
                            onUpdateEventFilters(metricIndex, eventIndex, updatedFilters);
                          }}
                          onRemove={onRemoveFilter}
                          teamId={teamId}
                          eventName={eventDef.name}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add Event button + menu */}
      <div className="relative mb-3">
        <button
          ref={addEventButtonRef}
          type="button"
          onClick={handleAddEventClick}
          className="w-full flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg text-left text-sm transition-colors"
          style={{
            borderColor: theme.border,
            color: theme.textMuted,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#4B57AA";
            e.currentTarget.style.color = theme.textPrimary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme.border;
            e.currentTarget.style.color = theme.textMuted;
          }}
        >
          <span className="text-gray-500">+</span>
          <span className="flex-1">Add Event</span>
        </button>
        {eventMenuOpen && (
          <div ref={eventMenuRef} className="absolute top-full left-0 right-0 mt-1 z-10">
            <EventsSelectorMenu
              availableEvents={
                Array.isArray(availableEvents)
                  ? availableEvents.filter(
                      (evt) =>
                        !(metric.events || []).some(
                          (e) =>
                            e.name === evt.name &&
                            (e.source || "event") === (evt.source || "event")
                        )
                    )
                  : []
              }
              onSelect={(name, source) => handleSelectEvent(name, source)}
              onClose={() => setEventMenuOpen(false)}
              maxHeight={220}
            />
          </div>
        )}
      </div>

      {/* Aggregation dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setAggregationOpen((o) => !o)}
          className="w-full flex items-center gap-2 px-3 py-2 border rounded-lg text-left text-sm transition-colors"
          style={{
            backgroundColor: theme.bgRaised,
            borderColor: theme.border,
            color: theme.textSecondary,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#4B57AA";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme.border;
          }}
        >
          <span className="text-gray-500">#</span>
          <span className="flex-1 truncate">
            {aggregations.find((a) => a.id === metric.aggregation)?.label ||
              (isTransactionOnly ? "Total Transactions" : "Total Events")}
          </span>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </button>
        {aggregationOpen && (
          <div
            ref={aggregationMenuRef}
            className="absolute top-full left-0 right-0 mt-1 z-10 border rounded-lg shadow-xl py-1 max-h-[240px] overflow-y-auto"
            style={{ backgroundColor: theme.bgRaised, borderColor: theme.border }}
          >
            {aggregations.map((agg) => (
              <button
                key={agg.id}
                type="button"
                onClick={() => handleAggregationChange(agg.id)}
                className="w-full px-3 py-2 text-left text-sm transition-colors"
                style={{ color: "#374151" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(45, 45, 45, 0.12)";
                  e.currentTarget.style.color = theme.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = theme.textSecondary;
                }}
              >
                <span className="flex-1 truncate">{agg.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Event Property Filter Module */}
      {isPropertyFilterOpen && selectedEventForFilter && (
        <EventPropertyFilterModule
          isOpen={isPropertyFilterOpen}
          onClose={() => {
            setIsPropertyFilterOpen(false);
            setSelectedEventForFilter(null);
            setSelectedEventIndex(null);
            setEditingPropertyFilter(null);
          }}
          triggerRef={
            editingPropertyFilter && propertyFilterButtonRefs.current[editingPropertyFilter.id]
              ? propertyFilterButtonRefs.current[editingPropertyFilter.id]
              : filterButtonRefs.current[`${metricIndex}-${selectedEventIndex}`]
          }
          eventName={selectedEventForFilter.eventName}
          eventId={selectedEventForFilter.id}
          teamId={teamId}
          filters={selectedEventIndex !== null ? (metric.events[selectedEventIndex]?.filters || []) : []}
          onFiltersChange={handleFiltersChange}
          existingFilter={editingPropertyFilter}
          firstTimeOnly={selectedEventIndex !== null ? (metric.events[selectedEventIndex]?.filters || []).some(f => f.category === 'first_time') : false}
          onFirstTimeChange={handleFirstTimeChange}
        />
      )}

      {/* Transaction Property Filter Module (for contractName.method metrics) */}
      {isTransactionFilterOpen && selectedEventForFilter && selectedEventForFilter.eventType === 'transaction' && (
        <TransactionPropertyFilterModule
          isOpen={isTransactionFilterOpen}
          onClose={() => {
            setIsTransactionFilterOpen(false);
            setSelectedEventForFilter(null);
            setSelectedEventIndex(null);
            setEditingPropertyFilter(null);
          }}
          triggerRef={
            editingPropertyFilter && propertyFilterButtonRefs.current[editingPropertyFilter.id]
              ? propertyFilterButtonRefs.current[editingPropertyFilter.id]
              : filterButtonRefs.current[`${metricIndex}-${selectedEventIndex}`]
          }
          contractName={selectedEventForFilter.eventName}
          contractId={null}
          teamId={teamId}
          filters={selectedEventIndex !== null ? (metric.events[selectedEventIndex]?.filters || []).filter(f => f.category !== 'first_time') : []}
          onFiltersChange={handleTransactionFiltersChange}
          existingFilter={editingPropertyFilter}
          firstTimeOnly={selectedEventIndex !== null ? (metric.events[selectedEventIndex]?.filters || []).some(f => f.category === 'first_time') : false}
          onFirstTimeChange={handleTransactionFirstTimeChange}
        />
      )}
    </div>
  );
}

